<template>
    <div>
        <div v-show="currentStep === 1 || !secondStepEnabled">
            <div
                v-if="
                    applicationFormContent[0]?.title &&
                    applicationFormContent[0]?.text &&
                    !hasCompletedFirstStep &&
                    currentStep === 1
                "
                class="pt-20"
            >
                <h2
                    class="font-heading uppercase text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-center text-teal mb-3"
                    v-text="applicationFormContent[0]?.title"
                ></h2>
                <div class="flex justify-center mb-5">
                    <p class="prose text-center" v-text="applicationFormContent[0]?.text"></p>
                </div>
            </div>
            <div v-show="!hasCompletedFirstStep" class="max-w-lg mx-auto">
                <applicationFormInput
                    v-for="(field, index) in steps[1]"
                    v-show="currentStep === 1"
                    v-bind="field"
                    ref="formInputs"
                    :key="getInputKey(field.step, index)"
                    v-model:valid="field.valid"
                    :value="field.value"
                    :index="index"
                    :active="activeQuestion === index"
                    :clickable="index <= lastAnsweredQuestion || !field.required || currentStepIsValid"
                    :total="steps[1].length"
                    @input="form[form.indexOf(field)].value = $event"
                    @active-changed="activeQuestion = $event"
                    @confirmed="handleInputConfirmedEvent(field)"
                ></applicationFormInput>
            </div>

            <section
                v-if="secondStepEnabledAndFirstStepFinished"
                class="py-20 text-gray opacity-100 transition duration-200"
            >
                <div class="row">
                    <div class="col-12">
                        <div>
                            <div class="font-bold leading-6 mb-4">
                                <span v-text="snippets.form_snippets_application_form_question_explanation"></span>
                                <a
                                    class="bg-transparant border-none text-teal underline hover:cursor-pointer hover:no-underline"
                                    :href="questionUrl"
                                    v-text="snippets.form_snippets_application_form_information_link"
                                ></a>
                            </div>
                            <div class="relative mb-4 grid gap-3 md:grid-cols-3">
                                <div class="md:col-span-2">
                                    <div
                                        class="active:ring-2 active:ring-teal-600 aspect-square bg-orange border-2 border-orange cursor-pointer duration-200 flex focus:ring-2 focus:ring-teal-600 h-16 hover:bg-transparent hover:border-2 hover:border-orange hover:text-orange items-center justify-between mb-4 p-5 rounded text-left text-white transition w-full"
                                        :disabled="isSubmitting"
                                        @click.prevent="submitForm('videoask')"
                                    >
                                        <div
                                            class="font-semibold mb-1.5"
                                            v-text="
                                                snippets.form_snippets_application_form_start_application_button_video
                                            "
                                        ></div>
                                        <i class="fa-long-arrow-right fa-light text-3xl"></i>
                                    </div>
                                    <div
                                        class="cursor-pointer rounded py-4 px-6 bg-transparent border-2 border-teal w-full mb-4 text-left transition duration-200 hover:border-2 hover:border-teal hover:bg-teal hover:text-white text-teal font-semibold relative flex justify-between focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600 items-center h-16"
                                        type="button"
                                        :disabled="isSubmitting"
                                        @click.prevent="submitForm('anyway')"
                                    >
                                        <div
                                            class="font-semibold mb-1.5"
                                            v-text="snippets.form_snippets_application_form_start_application_button"
                                        ></div>
                                        <i class="fa-long-arrow-right fa-light text-3xl"></i>
                                    </div>
                                    <div
                                        class="rounded py-4 px-6 bg-transparant border-2 border-teal w-full mb-4 text-left cursor-pointer transition duration-200 hover:border-2 hover:border-teal hover:bg-teal hover:text-white text-teal font-semibold relative flex justify-between focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600 items-center h-16"
                                        @click="goBackToFirstQuestions"
                                    >
                                        <span v-text="snippets.form_snippets_application_form_back_button"></span>
                                        <i class="fa-long-arrow-left fa-light text-3xl"></i>
                                    </div>
                                </div>
                                <div>
                                    <div class="overflow-hidden flex justify-center md:justify-start">
                                        <slot name="sollicitation-video-website"></slot>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section
                v-else-if="secondStepDisabledAndFirstStepFinished"
                class="py-20 text-gray transition duration-200 opacity-100"
            >
                <div class="row">
                    <div class="col-12">
                        <div class="leading-6 text-black text-center mb-4">
                            <span v-text="snippets.form_snippets_application_form_question_explanation"></span>
                            <a
                                class="bg-transparant border-none text-teal underline hover:cursor-pointer hover:no-underline"
                                :href="questionUrl"
                                v-text="snippets.form_snippets_application_form_information_link"
                            ></a>
                        </div>
                        <div class="relative mb-4 gap-3 grid md:grid-cols-3">
                            <div class="md:col-span-2">
                                <div
                                    class="active:ring-2 active:ring-teal-600 aspect-square bg-orange border-2 border-orange cursor-pointer duration-200 flex focus:ring-2 focus:ring-teal-600 h-16 hover:bg-transparent hover:border-2 hover:border-orange hover:text-orange items-center justify-between mb-4 p-5 rounded text-left text-white transition w-full"
                                    :disabled="isSubmitting"
                                    @click.prevent="submitForm('step2Video')"
                                >
                                    <div
                                        class="font-semibold mb-1.5 text-center"
                                        v-text="snippets.form_snippets_application_form_start_application_button_video"
                                    ></div>
                                    <i class="fa-long-arrow-right fa-light text-3xl"></i>
                                </div>
                                <div
                                    class="cursor-pointer rounded py-4 px-6 bg-transparent border-2 border-teal w-full mb-4 text-left transition duration-200 hover:border-2 hover:border-teal hover:bg-teal hover:text-white text-teal font-semibold relative flex justify-between focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600 items-center h-16"
                                    type="button"
                                    @click.prevent="submitForm('meeting')"
                                >
                                    <div
                                        class="font-semibold mb-1.5"
                                        v-text="snippets.form_snippets_application_form_start_application_button"
                                    ></div>
                                    <i class="fa-long-arrow-right fa-light text-3xl"></i>
                                </div>
                                <div
                                    class="cursor-pointer rounded py-4 px-6 bg-transparant border-2 border-teal w-full mb-4 text-left cursor-poiner transition duration-200 hover:border-2 hover:border-teal hover:bg-teal hover:text-white text-teal font-semibold relative flex justify-between focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600 items-center h-16"
                                    @click="goBackToFirstQuestions"
                                >
                                    <span v-text="snippets.form_snippets_application_form_back_button"></span>
                                    <i class="fa-long-arrow-left fa-light text-3xl"></i>
                                </div>
                            </div>
                            <div>
                                <div class="overflow-hidden flex justify-center md:justify-start">
                                    <slot name="sollicitation-video-website"></slot>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>

        <div v-if="currentStep === 2 && secondStepEnabled">
            <div class="max-w-lg mx-auto">
                <ApplicationFormInput
                    v-for="(field, index) in steps[2]"
                    v-bind="field"
                    ref="formInputs"
                    :key="getInputKey(field.step, index)"
                    v-model:valid="field.valid"
                    :value="field.value"
                    :index="index"
                    :active="activeQuestion === index"
                    :clickable="index <= lastAnsweredQuestion || !field.required || currentStepIsValid"
                    :total="steps[2].length"
                    @input="form[form.indexOf(field)].value = $event"
                    @active-changed="activeQuestion = $event"
                    @confirmed="handleInputConfirmedEvent(field)"
                ></ApplicationFormInput>
            </div>

            <div class="py-20 text-gray transition duration-200 opacity-100 max-w-3xl mx-auto">
                <div class="leading-6 mb-4 text-black text-center">
                    <span v-text="snippets.form_snippets_application_form_question_explanation"></span>
                    <a
                        class="bg-transparant border-none text-teal underline hover:cursor-pointer hover:no-underline"
                        :href="questionUrl"
                        v-text="snippets.form_snippets_application_form_information_link"
                    ></a>
                </div>

                <div class="relative mb-4 gap-3 grid md:grid-cols-3">
                    <div class="md:col-span-2">
                        <div
                            class="active:ring-2 active:ring-teal-600 aspect-square bg-orange border-2 border-orange cursor-pointer duration-200 flex focus:ring-2 focus:ring-teal-600 h-16 hover:bg-transparent hover:border-2 hover:border-orange hover:text-orange items-center justify-between mb-4 p-5 rounded text-left text-white transition w-full"
                            :disabled="isSubmitting"
                            @click.prevent="submitForm('step2Video')"
                        >
                            <div
                                class="font-semibold mb-1.5 text-center"
                                v-text="snippets.form_snippets_application_form_start_application_button_video"
                            ></div>
                            <i class="fa-long-arrow-right fa-light text-3xl"></i>
                        </div>
                        <div
                            class="cursor-pointer rounded py-4 px-6 bg-transparent border-2 border-teal w-full mb-4 text-left transition duration-200 hover:border-2 hover:border-teal hover:bg-teal hover:text-white text-teal font-semibold relative flex justify-between focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600 items-center h-16"
                            type="button"
                            :disabled="isSubmittingSecondStep || !allStepsAreValid"
                            @click.prevent="submitForm('meeting')"
                        >
                            <div
                                class="font-semibold mb-1.5"
                                v-text="snippets.form_snippets_application_form_start_application_button"
                            ></div>
                            <i class="fa-long-arrow-right fa-light text-3xl"></i>
                        </div>
                        <div
                            class="cursor-pointer rounded py-4 px-6 bg-transparent border-2 border-teal w-full mb-4 text-left transition duration-200 hover:border-2 hover:border-teal hover:bg-teal hover:text-white text-teal font-semibold relative flex justify-between focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600 items-center h-16"
                            @click="goBackToFirstQuestions"
                        >
                            <span v-text="snippets.form_snippets_application_form_back_button"></span>
                            <i class="fa-long-arrow-left fa-light text-3xl"></i>
                        </div>
                    </div>
                    <div>
                        <div class="overflow-hidden flex justify-center md:justify-start">
                            <slot name="sollicitation-video-website"></slot>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <input type="hidden" name="country_iso" :value="countryIso" />
        <input type="hidden" name="vacancy_title" :value="vacancyTitleValue" />
        <input type="hidden" name="adult_year" :value="selectedAdultYear" />
        <input type="hidden" name="submit_type" :value="submitType" />
        <input type="hidden" name="submit_type_description" :value="submitTypeDescription" />
        <input type="hidden" name="location" :value="location" />
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, inject, watch } from "vue";
import axios from "axios";
import { pushGAEvent } from "@/analyticsHelper";
import ApplicationFormInput from "./ApplicationFormInput";
import formAnimatieWerk from "../forms/animatiewerk.nl";
import formZomerjobs from "../forms/zomerjobs.nl";

let FORM_DATA = formZomerjobs;

if (window.location.href.includes("animatiewerk")) {
    FORM_DATA = formAnimatieWerk;
}

const snippets = inject("statamic_snippets");
const params = new URL(document.location).searchParams;
const hasVacancyTitle = params.has("vacancy_title");

const props = defineProps({
    countryIso: {
        type: String,
        default: "NL",
    },
    initialValues: {
        type: Object,
        default: () => {},
    },
    employeePicture: {
        type: String,
        default: "",
    },
    employeeName: {
        type: String,
        default: "",
    },
    employeeMessageSoundUrl: {
        type: String,
        default: "",
    },
    chatText: {
        type: String,
        default: "",
    },
    chatTextEighteen: {
        type: String,
        default: "",
    },
    buttonLeft: {
        type: String,
        default: "",
    },
    buttonRight: {
        type: String,
        default: "",
    },
    questionUrl: {
        type: String,
        default: "",
    },
    secondStepEnabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    applicationFormContent: {
        type: Array,
        required: true,
    },
});

const currentStep = ref(1);
const selectedAdultYear = ref(null);
const submitType = ref(null);
const location = ref(window.location.href);
const activeQuestion = ref(0);
const isSubmitting = ref(false);
const isSubmittingSecondStep = ref(false);
const lastAnsweredQuestion = ref(0);
const form = ref(FORM_DATA);
const vacancyTitleValue = ref(params.get("vacancy_title") ?? null);
const hasCompletedFirstStep = ref(false);
const alreadyPushedFirstStepCompleted = ref(false);

const formInputs = ref(null);

const steps = computed(() => {
    const steps = {};
    form.value.forEach((question) => {
        const stepName = question.step;

        if (!steps[stepName]) {
            steps[stepName] = [];
        }

        steps[stepName].push(question);
    });
    return steps;
});

const currentStepIsValid = computed(
    () => steps.value[currentStep.value].findIndex(({ valid }) => valid === false) === -1
);

const allStepsAreValid = computed(
    () =>
        steps.value[currentStep.value].findIndex(({ required, valid }) => {
            if (required) {
                return !valid;
            }

            return false;
        }) === -1
);

const secondStepDisabledAndFirstStepFinished = computed(
    () => !props.secondStepEnabled && (currentStep.value === 2 || hasCompletedFirstStep.value)
);

const secondStepEnabledAndFirstStepFinished = computed(
    () => props.secondStepEnabled && (currentStep.value === 2 || hasCompletedFirstStep.value)
);

const isLastQuestion = computed(() => activeQuestion.value >= steps.value[currentStep.value].length - 1);

const updateLastAnsweredQuestion = () => {
    const firstNotFilledIndex = steps.value[currentStep.value].findIndex(({ value }) => value === "" || value === null);

    if (firstNotFilledIndex === -1) {
        // Return the index of the last question

        lastAnsweredQuestion.value = steps.value[currentStep.value].length - 1;
    }

    lastAnsweredQuestion.value = firstNotFilledIndex;
};

const submitTypeDescription = computed(() => {
    switch (submitType.value) {
        case "step2":
        case "anyway":
            return "Niet gesolliciteerd";
        case "sendInfo":
            return "Niet gesolliciteerd";
        case "internalWork":
            return "Intakegesprek Nederland";
        case "nonAdult":
            return "Geen 18";
        case "step2submit":
        case "meeting":
            return `Kennismakingsgesprek, ${props.countryIso}`;
        case "step2Video":
        case "videoask":
            return `Videoask, ${props.countryIso}`;
        default:
            return "Onbekend hoe... huh...?";
    }
});

const setInitialValuesFromStorage = () => {
    form.value = form.value.map((question) => {
        if (localStorage[`application_form.${question.name}`] && question.type !== "file") {
            question.value = localStorage[`application_form.${question.name}`];
            return question;
        }

        return question;
    });
};

const clearValuesFromStorageByStep = (step) => {
    steps.value[step].map((question) => {
        window.localStorage.removeItem(`application_form.${question.name}`);
        return question;
    });
};

const setInitialValues = () => {
    Object.keys(props.initialValues).forEach((key) => {
        if (!props.initialValues[key]) {
            return;
        }
        const question = form.value.find(({ name }) => name === key);
        question.value = props.initialValues[key];
    });
};

const goToStep = (step) => {
    currentStep.value = step;
    activeQuestion.value = 0;
    updateLastAnsweredQuestion();

    nextTick(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    });
};

onMounted(() => {
    setInitialValuesFromStorage();
    setInitialValues();

    const formLastAnsweredQuestion = form.value.find(({ value }) => value === "");
    if (formLastAnsweredQuestion) {
        currentStep.value = formLastAnsweredQuestion.step;
    }

    updateLastAnsweredQuestion();

    nextTick(() => {
        activeQuestion.value = steps.value[currentStep.value].findIndex(({ value }) => value === "");

        if (activeQuestion.value === -1 || form.value[activeQuestion.value].step !== currentStep.value) {
            return;
        }

        formInputs.value[activeQuestion.value].questionRef.focus();
    });
});

/**
 * step2: Vragenlijst
 * step2Video: Video
 * @param type
 */
const submitForm = (type) => {
    submitType.value = type;

    if (type === "step2" || type === "anyway") {
        currentStep.value = 2;
        activeQuestion.value = 0;
        updateLastAnsweredQuestion();

        nextTick(() => {
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        });

        nextTick(() => {
            const applicationFormElement = document.getElementById("application-form");
            axios.post(applicationFormElement.getAttribute("action"), new FormData(applicationFormElement));
        });

        return;
    }

    if (type === "step2submit" || type === "step2Video" || type === "meeting" || type === "videoask") {
        isSubmittingSecondStep.value = true;
        clearValuesFromStorageByStep(1);
        clearValuesFromStorageByStep(2);
    }

    if (type === "step2Video" || type === "videoask") {
        activeQuestion.value = 0;
        clearValuesFromStorageByStep(1);
        clearValuesFromStorageByStep(2);
    }

    nextTick(() => {
        document.getElementById("application-form").submit();
    });
};

const goBackToFirstQuestions = () => {
    hasCompletedFirstStep.value = false;
    goToStep(1);
};

const handleInputConfirmedEvent = (field) => {
    // const nextIndex = index + 1;
    const nextIndex = form.value.indexOf(field) + 1;

    const component = formInputs;
    const nextField = form.value[nextIndex];

    if (!nextField) {
        return;
    }

    if (nextField.step !== field.step) {
        if (field.step === 1) {
            hasCompletedFirstStep.value = true;

            nextTick(() => {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            });
        }
        return;
    }

    if (!component.value[nextIndex]) {
        return;
    }

    updateLastAnsweredQuestion();

    component.value[nextIndex].questionRef.focus();
    component.value[nextIndex].focus();
};

const getInputKey = (step, index) => `step${step}_${index}`;

watch(hasCompletedFirstStep, (newValue) => {
    if (!newValue || alreadyPushedFirstStepCompleted.value) {
        return;
    }

    alreadyPushedFirstStepCompleted.value = true;

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get("ref")) {
        pushGAEvent("application_progress_step1_filled_referred", { referrer: urlParams.get("ref") });
    } else {
        pushGAEvent("application_progress_step1_filled", {});
    }
});
</script>
